'use client'

import { IconName } from 'lucide-react/dynamic'
import IconBadge from '../common/IconBadge'
import { AmenitySheet } from '../dialog/AmenitySheet'
import { Card, CardContent } from '../ui/card'
import { HotelDetailsSheet } from '../dialog/HotelDetailsSheet'
import { Hotel } from '@/payload-types'
import { ArrowUpRight } from 'lucide-react'
import { ImageBlock } from '../common/ImageBlock'
import { Link } from '@/i18n/navigation'
import { useParams } from 'next/navigation'
import { useTranslations } from 'next-intl'

type ConferenceInfo = {
  image?: Hotel['conferenceImage']
} | null

const AmenitiesAndDetails = ({
  hotel,
  amenities,
  details,
  conference,
}: {
  hotel: Hotel
  amenities: Hotel['amenities']
  details: Hotel['details']
  conference?: ConferenceInfo
}) => {
  const params = useParams()
  const t = useTranslations()

  return (
    <div>
      <h3 className='mb-3'>{t('at_the_hotel')}</h3>

      <div className='space-y-4 lg:grid lg:grid-cols-2 lg:gap-4 lg:space-y-0'>
        <Card className='border-none'>
          <CardContent className='flex h-full flex-col justify-between space-y-4'>
            <div className='space-y-4'>
              {amenities?.slice(0, 6).map((amenity) => {
                if (typeof amenity.icon === 'number') return null

                return (
                  <div key={amenity.id} className='flex items-center space-x-2'>
                    <IconBadge dynamic={amenity.icon.icon as IconName} />
                    <p>{amenity.icon.title}</p>
                  </div>
                )
              })}
            </div>

            <AmenitySheet title={'At the hotel'} amenities={amenities} hotel={hotel} />
          </CardContent>
        </Card>

        {details && details.length > 0 && (
          <Card className='border-none'>
            <CardContent className='space-y-4'>
              {details?.map((detail) => {
                if (!detail) return null

                return (
                  <div key={detail.id}>
                    <HotelDetailsSheet content={detail} />
                  </div>
                )
              })}

              {conference && (
                <div>
                  <Link
                    className='hover:bg-accent flex h-full w-full items-center justify-between rounded-md border bg-transparent p-1.5! transition-colors'
                    href={{
                      pathname: '/hotels/[country]/[city]/[hotel]/conferences-meetings',
                      params: {
                        country: params.country as string,
                        city: params.city as string,
                        hotel: params.hotel as string,
                      },
                    }}
                  >
                    <div className='flex h-12 items-center space-x-4'>
                      {conference.image &&
                        (conference.image.external || conference.image.internal) && (
                          <div className='relative aspect-square size-12 h-full overflow-hidden rounded'>
                            <ImageBlock imageBlock={conference.image} />
                          </div>
                        )}

                      <p className='font-medium'>{t('conference_and_meetings')}</p>
                    </div>

                    <ArrowUpRight className='text-muted-foreground size-6' strokeWidth={1.5} />
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}

export default AmenitiesAndDetails
